import { useQueryParams } from 'use-query-params'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import React, { Key, ReactNode, useMemo, useState } from 'react'

import { Icon, Show } from '@app/components/util'
import { LntButton, LntDropdown } from '@app/components/base'
import { ContentLayout } from '@app/layouts'
import { CopyArchivesListModal } from '@app/modals'
import { ExtendedTable, TableSettingsToolbar, SubheaderToolbarDivider, PermissionWrapper } from '@app/components'

import { replaceIdPath } from '@app/utils/navigation'
import { copyArchivesItem, getArchivesLists } from '@app/api/routes/monitoringLists'
import {
  useExtendedNavigate,
  useNotification,
  usePaginationQueryParams,
  useTableSearch,
  useTableSetting,
  useToggle
} from '@app/hooks'
import { exportArchivesItemFile, exportCompareArchivesFile, exportMonitoringArchivesListsFile } from '@app/utils/export'
import { getExportFilesDropdownMenu } from '@app/utils'
import { objectHasEveryNullableProperties } from '@app/utils/object'

import { FILE_TYPES } from '@app/api/constants'
import { REGISTRY_PAGE } from '@app/constants/page'
import { PERMISSION } from '@app/constants/permission'
import { MONITORINGS_LISTS_KEY } from '@app/constants/queryKeys'
import { BUTTON_TYPE, SIZE } from '@app/constants'
import { TABLE_SETTING_KEYS } from '@app/constants/tableSettingKeys'
import { ICON_NAME } from '@app/constants/icon'

import {
  CopyArchivesItem,
  GetArchivesListsParams,
  ExportMonitoringArchivesList
} from '@app/api/routes/monitoringLists/types'

import { TopFilters } from './ui'
import { getColumns, adaptRegistryArchiveListsTableData, adaptGetArchivesListsParams } from './lib'
import { topFiltersQuerySchema, tableSearchQuerySchema, columnsSearchSchema } from './configs'

import type { FiltersParams } from '@app/types/filter'
import type { FileType } from '@app/types'

const columns = getColumns()

export const ArchivesListsTab: React.FC = () => {
  const queryClient = useQueryClient()
  const navigate = useExtendedNavigate()
  const notification = useNotification()
  const [queryFilters, setQueryFilters] = useQueryParams(topFiltersQuerySchema)
  const { paginationParams, pagination, setPagination, resetCurrentPage } = usePaginationQueryParams()

  const [selectedIds, setSelectedIds] = useState<number[]>([])

  const buttonLoadingExportToggle = useToggle()

  const {
    value: isCopyListModalOpen,
    setUntoggled: handleCloseCopyListModal,
    setToggled: handleOpenCopyListModal
  } = useToggle()

  const listsDataParams = useMemo(
    () => ({ ...paginationParams, ...queryFilters }),
    [paginationParams, queryFilters]
  ) as GetArchivesListsParams

  const { data: registryArchivesListsTableData, isLoading: isArchivesListsDataLoading } = useQuery({
    enabled: !objectHasEveryNullableProperties(queryFilters),
    queryKey: [MONITORINGS_LISTS_KEY.archivesListItems, listsDataParams],
    queryFn: async () => await getArchivesLists(listsDataParams),
    select: archivesListsData => adaptRegistryArchiveListsTableData(archivesListsData)
  })

  const copyArchivesItemMutation = useMutation({
    mutationFn: copyArchivesItem,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [MONITORINGS_LISTS_KEY.archivesListItems] })

      notification.success({
        message: 'Список успешно скопирован.'
      })
    }
  })

  const exportMonitoringArchivesListsFileMutation = useMutation(
    async ({ fileName, params }: { fileName: string, params: ExportMonitoringArchivesList }) =>
      await exportMonitoringArchivesListsFile(fileName, params)
  )

  const handleFiltersChange = (filters: FiltersParams): void => {
    setQueryFilters(filters)
    resetCurrentPage()
  }

  const handleSelectedRowChange = (selectedKeys: Key[]): void => {
    setSelectedIds(selectedKeys.map(Number))
  }

  const handlePaginationChange = (current: number, pageSize: number): void => setPagination({ current, pageSize })

  const handleCopyList = (data: CopyArchivesItem): void => {
    if (!selectedIds.length) return

    const archiveMonitoringListId = selectedIds[0]

    if (isNaN(archiveMonitoringListId)) return

    copyArchivesItemMutation.mutate({ ...data, archiveMonitoringListId }, { onSettled: handleCloseCopyListModal })
  }

  const handleCompareDownloadFile = (): void => {
    const fileName = 'Сравнение'

    const id = selectedIds[0]

    if (id === undefined) return

    exportCompareArchivesFile(fileName, { id, file: FILE_TYPES.xlsx })
  }

  const handleExportArchivesItem = async (fileType: FileType): Promise<void> => {
    buttonLoadingExportToggle.setToggled()

    const fileName = 'Архивный_список'
    const id = selectedIds[0]

    if (id === undefined) return

    try {
      await exportArchivesItemFile(fileName, { id, file: fileType })
    } finally {
      buttonLoadingExportToggle.setUntoggled()
    }
  }

  const handleNavigateToInnerPageBySelected = (): void => {
    const id = selectedIds[0]
    const path = replaceIdPath(REGISTRY_PAGE.registryArchivesId, id)

    navigate(path)
  }

  const handleNavigateToInnerPage = (key: number): void => {
    if (!key) return
    const path = replaceIdPath(REGISTRY_PAGE.registryArchivesId, key)

    navigate(path)
  }

  const {
    items,
    defaultItems,
    columns: modifiedColumns,
    onModify
  } = useTableSetting(TABLE_SETTING_KEYS.archivesLists, columns)

  const {
    columns: columnsWithSearchTool,
    filteredDataSource: filteredMonitoringsListsArchivesDataSource,
    isSearchToolOpened,
    toggleSearchTool
  } = useTableSearch({
    columns: modifiedColumns,
    dataSource: registryArchivesListsTableData?.dataSource,
    columnsSearchSchema,
    tableSearchQuerySchema
  })

  const exportDropdownItems = getExportFilesDropdownMenu(
    'Архивный список',
    [FILE_TYPES.xlsx, FILE_TYPES.csv],
    handleExportArchivesItem
  )

  const handleExportAllItemClick = async (file: FileType): Promise<void> => {
    const params: ExportMonitoringArchivesList = {
      ...adaptGetArchivesListsParams(queryFilters),
      file
    }

    const fileName = 'Архивный_список'

    exportMonitoringArchivesListsFileMutation.mutate({ fileName, params })
  }

  const toolbar: ReactNode[] = [
    <Show
      key='actions-buttons'
      condition={ selectedIds.length === 1 }
    >
      <LntButton
        type={ BUTTON_TYPE.text }
        size={ SIZE.small }
        onClick={ handleNavigateToInnerPageBySelected }
      >
        Просмотр
      </LntButton>

      <SubheaderToolbarDivider />

      <PermissionWrapper requiredPermissions={ [PERMISSION.createAndCopyMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleOpenCopyListModal }
        >
          Копировать
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.exportMonitoringList] }>
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          onClick={ handleCompareDownloadFile }
        >
          Сравнить
        </LntButton>
      </PermissionWrapper>

      <PermissionWrapper requiredPermissions={ [PERMISSION.exportMonitoringList] }>
        <LntDropdown
          trigger={ ['click'] }
          items={ exportDropdownItems }
        >
          <LntButton
            type={ BUTTON_TYPE.text }
            size={ SIZE.small }
            loading={ buttonLoadingExportToggle.value }
          >
            Экспорт
            <Icon name={ ICON_NAME.arrowDown } />
          </LntButton>
        </LntDropdown>
      </PermissionWrapper>

      <SubheaderToolbarDivider />
    </Show>,

    <PermissionWrapper
      key='export-all-lists_button'
      requiredPermissions={ [PERMISSION.exportMonitoringList] }
    >
      <LntDropdown
        trigger={ ['click'] }
        items={ getExportFilesDropdownMenu(
          'Архивный список',
          [FILE_TYPES.xlsx, FILE_TYPES.csv],
          handleExportAllItemClick
        ) }
      >
        <LntButton
          type={ BUTTON_TYPE.text }
          size={ SIZE.small }
          loading={ exportMonitoringArchivesListsFileMutation.isLoading }
        >
          Экспорт всех
          <Icon name={ ICON_NAME.arrowDown } />
        </LntButton>
      </LntDropdown>
    </PermissionWrapper>,

    <TableSettingsToolbar
      key='table-settings-toolbar'
      tableSettingButtonProps={ { items, defaultItems, onAccept: onModify } }
      tableSearchButtonProps={ { active: isSearchToolOpened, onClick: toggleSearchTool } }
    />
  ]

  return (
    <ContentLayout>
      <TopFilters
        onSearch={ handleFiltersChange }
        initialFilters={ queryFilters as FiltersParams }
      />

      <ExtendedTable
        loading={ isArchivesListsDataLoading }
        columns={ columnsWithSearchTool }
        dataSource={ filteredMonitoringsListsArchivesDataSource }
        toolbar={ toolbar }
        rowSelection={ {
          onChange: handleSelectedRowChange
        } }
        pagination={ {
          ...pagination,
          total: registryArchivesListsTableData?.count,
          onChange: handlePaginationChange
        } }
        onRowDoubleClick={ (_, record) => handleNavigateToInnerPage(record.key) }
        scroll={ { x: 'max-content' } }
      />

      <CopyArchivesListModal
        open={ isCopyListModalOpen }
        onCancel={ handleCloseCopyListModal }
        onSubmit={ handleCopyList }
        title='Копировать список в'
      />
    </ContentLayout>
  )
}
